import { GoogleLogin } from "@react-oauth/google";
import { useReturnAfterSignIn } from "hooks/use-return-after-sign-in";
import { useDispatch } from "react-redux";
import { setCookie } from "utils/cookieManagement";
import { 
  setIsUserLoggedIn, 
  setAuthType, 
  setUserId, 
  setUserNickname,
  setUserEmail,
  setUserAvatar,
  setGoogleData,
  AuthType 
} from "redux/slices/userDataSlice";
import { googleLogin, getAccountDetails } from "api/GoogleAuth";
import { storeUserApiKey } from "utils/auth/apiKeyStore";
import { trackUserLogin, trackUserSignup } from "../../../../../utils/analytics";

const GoogleLoginButton = () => {
  const returnAfterSignIn = useReturnAfterSignIn();
  const dispatch = useDispatch();

  const successAuthHandle = async (credentialResponse: any) => {
    const { credential } = credentialResponse;
    if (credential) {
      try {
        // console.log('🔄 Sending Google authorization request...');
        const response = await googleLogin(credential);
        
        if (response.status === 'success') {
          // console.log('✅ Google authorization successful:', response);
          
          // Send event to Yandex Metrica
          if (response.is_new_user) {
            trackUserSignup('google');
          } else {
            trackUserLogin('google');
          }
          
          // Key first: it refuses a non-user key before anything says "logged in".
          storeUserApiKey(dispatch, response.api_key);

          // Save data to Redux store
          dispatch(setIsUserLoggedIn(true));
          dispatch(setAuthType(AuthType.google));
          dispatch(setUserId(response.user_id));
          
          // Save to localStorage for persistence
          localStorage.setItem('user_id', response.user_id.toString());
          localStorage.setItem('auth_type', AuthType.google);
          
          // Get full account data
          try {
            const accountDetails = await getAccountDetails(response.api_key);
            if (accountDetails.status === 'success') {
              const { google_data, telegram_data } = accountDetails.data;
              
              // Update Redux with full data
              if (google_data) {
                dispatch(setGoogleData(google_data));
                dispatch(setUserNickname(`${google_data.given_name} ${google_data.family_name}`));
                dispatch(setUserEmail(google_data.email));
                dispatch(setUserAvatar(google_data.picture));
              } else if (telegram_data) {
                dispatch(setUserNickname(telegram_data.username));
              }
              
              // Save data to localStorage
              localStorage.setItem('google_data', JSON.stringify(google_data));
              localStorage.setItem('user_nickname', google_data?.given_name + ' ' + google_data?.family_name || telegram_data?.username || '');
              localStorage.setItem('user_email', google_data?.email || '');
            }
          } catch (error) {
            // console.error('Error fetching account data:', error);
          }
          
          // Save to cookie for compatibility
          if (process.env.NODE_ENV === "development") {
            setCookie("ses_userdata", JSON.stringify(response), false, 1);
          }
          
          // Back to the page that required sign-in, else home
          returnAfterSignIn();
        } else {
          // console.error('❌ Unexpected server response:', response);
          alert('Authorization error: ' + (response.message || 'Unknown error'));
        }
      } catch (error: any) {
        // console.error('❌ Google authorization failed:', error);
        
        // Handle different error types
        if (error.response) {
          const status = error.response.status;
          const message = error.response.data?.message || error.response.statusText;
          
          switch (status) {
            case 401:
              alert('Invalid Google token. Please try logging in again.');
              break;
            case 400:
              alert('Request error: ' + message);
              break;
            case 503:
              alert('Service temporarily unavailable. Please try again later.');
              break;
            case 500:
              alert('Server error. Please try again later.');
              break;
            default:
              alert('Authorization error: ' + message);
          }
        } else if (error.request) {
          alert('No connection to server. Please check your internet connection.');
        } else {
          alert('Error: ' + error.message);
        }
      }
    }
  };

  return (
    <GoogleLogin
      onSuccess={successAuthHandle}
      onError={() => {
        // console.log("Login Failed");
      }}
    />
  );
};

export default GoogleLoginButton;
